import React, { PureComponent } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { compose } from "redux";
import { Modal, Button, Image, Row, Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link, withRouter } from "react-router-dom";
import superagent from "superagent";
import withImmutablePropsToJS from "with-immutable-props-to-js";

import Icon from "storefront/features/shared/icons";
import Countdown from "storefront/features/shared/components/Countdown";
import { updateUserAuctionData } from "storefront/features/shared/components/Header/actions";
import { getUserAuctionData, getCustomerId } from "storefront/features/shared/components/Header/selectors";
import { getActiveAuctions } from "storefront/features/account/selectors";
import { updateCustomerActiveAuction } from "storefront/features/account/actions";
import { subscribePusher } from "storefront/features/shared/utils";
import {
  buildQuickViewPath,
  buildSuggestionUrl,
  productToSuggestionUpdates,
  setOptOut,
  suggestionToProduct,
} from "../utils";
import SuggestionBidButton from "./SuggestionBidButton";
import {
  SuggestionCard,
  SuggestionImageWrap,
  SuggestionMetaRow,
  SuggestionTimer,
  SuggestionTitle,
  SuggestionTitleLink,
} from "./BiddingSuggestionsModal.styled";

class BiddingSuggestionsModal extends PureComponent {
  state = {
    suggestions: [],
    loading: true,
  };

  pusherCleanups = [];

  componentDidMount() {
    if (this.props.show) {
      this.fetchSuggestions();
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.show && this.props.show) {
      this.fetchSuggestions();
    }
  }

  componentWillUnmount() {
    this.unsubscribeFromSuggestions();
  }

  fetchSuggestions = () => {
    this.unsubscribeFromSuggestions();
    this.setState({ loading: true });

    superagent
      .get("/api/bidding_suggestions")
      .then((response) => {
        const suggestions = response.body.suggestions || [];
        this.setState({ suggestions, loading: false }, () => {
          this.subscribeToSuggestions(suggestions);
        });
        if (suggestions.length === 0) {
          this.handleClose();
        }
      })
      .catch(() => {
        this.setState({ loading: false });
        this.handleClose();
      });
  };

  subscribeToSuggestions = (suggestions) => {
    const {
      updateAuctionData,
      userAuctionData,
      activeAuctions,
      updateCustomerActiveAuction,
      customerId,
    } = this.props;

    suggestions.forEach((suggestion) => {
      const product = suggestionToProduct(suggestion);
      const cleanup = subscribePusher({
        product,
        updateProduct: this.updateSuggestionFromProduct,
        updateQuickView: () => {},
        updateSearchProduct: () => {},
        updateAuctionData,
        userAuctionData,
        activeAuctions,
        updateCustomerActiveAuction,
        customerId,
      });
      this.pusherCleanups.push(cleanup);
    });
  };

  unsubscribeFromSuggestions = () => {
    this.pusherCleanups.forEach((cleanup) => cleanup());
    this.pusherCleanups = [];
  };

  updateSuggestionFromProduct = (product) => {
    const updates = productToSuggestionUpdates(product);

    this.setState((prev) => ({
      suggestions: prev.suggestions.map((suggestion) =>
        suggestion.bid_auction_id === product.bidAuctionId
          ? { ...suggestion, ...updates }
          : suggestion
      ),
    }));
  };

  handleClose = () => {
    this.unsubscribeFromSuggestions();
    const { onClose } = this.props;
    if (onClose) {
      onClose();
    }
  };

  handleOptOut = () => {
    setOptOut();
    this.handleClose();
  };

  renderSuggestion = (suggestion) => {
    const { location } = this.props;
    const {
      shopify_product_id,
      bid_auction_id,
      title,
      handle,
      image_url,
      end_at,
    } = suggestion;
    const linkTo = buildSuggestionUrl(
      buildQuickViewPath(location.pathname, handle, bid_auction_id),
      shopify_product_id
    );

    return (
      <Col key={shopify_product_id} md={4} className="mb-3 d-flex">
        <SuggestionCard>
          <Link to={linkTo} className="text-decoration-none text-dark">
            <SuggestionImageWrap>
              {image_url && (
                <Image
                  src={image_url}
                  alt={title}
                  fluid
                  className="d-block mx-auto"
                />
              )}
            </SuggestionImageWrap>
          </Link>

          <SuggestionMetaRow>
            <SuggestionTitleLink as={Link} to={linkTo}>
              <SuggestionTitle>{title}</SuggestionTitle>
            </SuggestionTitleLink>
            {end_at && (
              <SuggestionTimer>
                <Countdown endDate={end_at} />
              </SuggestionTimer>
            )}
          </SuggestionMetaRow>

          <SuggestionBidButton suggestion={suggestion} />
        </SuggestionCard>
      </Col>
    );
  };

  render() {
    const { show } = this.props;
    const { suggestions, loading } = this.state;

    if (!show || loading) {
      return null;
    }

    return (
      <Modal show={show} onHide={this.handleClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Auctions suggestions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            These auctions are closing soon — place your bid before time runs out.
          </p>
          <Row className="align-items-stretch">{suggestions.map(this.renderSuggestion)}</Row>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between w-100">
          <div className="d-flex align-items-center">
            <Button
              variant="link"
              className="text-muted p-0 small"
              onClick={this.handleOptOut}
            >
              Opt-Out
            </Button>
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="bidding-suggestions-opt-out-tooltip">
                  Suggestions will be disabled for next 30 days
                </Tooltip>
              }
            >
              <span className="text-muted ml-1 d-inline-flex" style={{ cursor: "help" }}>
                <Icon type="questionMarkIcon" />
              </span>
            </OverlayTrigger>
          </div>
          <Button variant="secondary" onClick={this.handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  activeAuctions: getActiveAuctions,
  userAuctionData: getUserAuctionData,
  customerId: getCustomerId,
});

const mapDispatchToProps = (dispatch) => ({
  updateAuctionData: (data) => {
    dispatch(updateUserAuctionData(data));
  },
  updateCustomerActiveAuction: (data) => {
    dispatch(updateCustomerActiveAuction(data));
  },
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withImmutablePropsToJS,
  withRouter
)(BiddingSuggestionsModal);
